import { algorithmExercises } from "~/features/computer/data/exercises/algorithm.exercises";
import type { ExerciseLong, ExerciseMcq, ExerciseShort, TopicExercises } from "~/features/computer/data/exercises/exercise.types";
import { expressionsExercises } from "~/features/computer/data/exercises/expressions.exercises";
import { loopsExercises } from "~/features/computer/data/exercises/loops.exercises";
import { computerMcqChapters } from "~/features/computer/data/mcq-registry";
import { operatorsExercises } from "~/features/computer/data/exercises/operators.exercises";
import { verifiedTopicExercises } from "~/features/computer/data/exercises/mcq.units";
import { variablesExercises } from "~/features/computer/data/exercises/variables.exercises";
import { dataAnalyticsExercises } from "~/features/computer/data/da/da.exercises";
import { dsExercises } from "~/features/computer/data/ds/ds.exercises";
import { sdlcExercises } from "~/features/computer/data/sdlc/sdlc.exercises";
import type { ComputerMcqUnit } from "~/features/computer/types/computer";

export type PracticeUnit = ComputerMcqUnit;

export type PracticeMcq = ExerciseMcq & {
  unit: PracticeUnit;
  unitLabel: string;
  topic: string;
  topicLabel: string;
  conceptTo: string;
  chapterTo: string;
};

export type PracticeShort = ExerciseShort & {
  unit: PracticeUnit;
  unitLabel: string;
  topic: string;
  topicLabel: string;
  conceptTo: string;
  chapterTo: string;
};

export type PracticeLong = ExerciseLong & {
  unit: PracticeUnit;
  unitLabel: string;
  topic: string;
  topicLabel: string;
  conceptTo: string;
  chapterTo: string;
};

type PracticeSource = {
  unit: PracticeUnit;
  unitLabel: string;
  topic: string;
  topicLabel: string;
  conceptTo: string;
  chapterTo: string;
  exercises: TopicExercises;
};

export const practiceUnits = [
  { value: "all", label: "All" },
  { value: "python", label: "Python" },
  { value: "sdlc", label: "SDLC" },
  { value: "algorithm", label: "Algorithms" },
  { value: "data-structures", label: "DS" },
  { value: "data-analytics", label: "DA" },
  { value: "emerging-tech", label: "Emerging" },
  { value: "legal-ethics", label: "Ethics" },
  { value: "digital-literacy", label: "Research" },
  { value: "entrepreneurship", label: "Business" },
] as const;

const practiceSources: PracticeSource[] = [
  {
    unit: "python",
    unitLabel: "Python",
    topic: "variables",
    topicLabel: "Variables",
    conceptTo: "/11th/computer/python/notes/",
    chapterTo: "/11th/computer/python/",
    exercises: variablesExercises,
  },
  {
    unit: "python",
    unitLabel: "Python",
    topic: "operators",
    topicLabel: "Operators",
    conceptTo: "/11th/computer/python/notes/",
    chapterTo: "/11th/computer/python/",
    exercises: operatorsExercises,
  },
  {
    unit: "python",
    unitLabel: "Python",
    topic: "expressions",
    topicLabel: "Expressions",
    conceptTo: "/11th/computer/python/notes/",
    chapterTo: "/11th/computer/python/",
    exercises: expressionsExercises,
  },
  {
    unit: "python",
    unitLabel: "Python",
    topic: "loops",
    topicLabel: "Loops",
    conceptTo: "/11th/computer/python/notes/",
    chapterTo: "/11th/computer/python/",
    exercises: loopsExercises,
  },
  {
    unit: "sdlc",
    unitLabel: "SDLC",
    topic: "sdlc",
    topicLabel: "SDLC",
    conceptTo: "/11th/computer/sdlc/",
    chapterTo: "/11th/computer/sdlc/",
    exercises: verifiedTopicExercises.sdlc ?? sdlcExercises,
  },
  {
    unit: "algorithm",
    unitLabel: "Algorithms",
    topic: "algorithm",
    topicLabel: "Algorithm",
    conceptTo: "/11th/computer/algorithm/",
    chapterTo: "/11th/computer/algorithm/",
    exercises: verifiedTopicExercises.algorithm ?? algorithmExercises,
  },
  {
    unit: "data-structures",
    unitLabel: "Data Structures",
    topic: "data-structures",
    topicLabel: "Data Structures",
    conceptTo: "/11th/computer/data-structures/",
    chapterTo: "/11th/computer/data-structures/",
    exercises: verifiedTopicExercises["data-structures"] ?? dsExercises,
  },
  {
    unit: "data-analytics",
    unitLabel: "Data Analytics",
    topic: "data-analytics",
    topicLabel: "Data Analytics",
    conceptTo: "/11th/computer/data-analytics/",
    chapterTo: "/11th/computer/data-analytics/",
    exercises: verifiedTopicExercises["data-analytics"] ?? dataAnalyticsExercises,
  },
  {
    unit: "emerging-tech",
    unitLabel: "Emerging",
    topic: "cloud",
    topicLabel: "Cloud & Blockchain",
    conceptTo: "/11th/computer/emerging-tech/",
    chapterTo: "/11th/computer/emerging-tech/",
    exercises: verifiedTopicExercises["emerging-tech"],
  },
  {
    unit: "legal-ethics",
    unitLabel: "Legal & Ethics",
    topic: "legal-ethics",
    topicLabel: "Legal & Ethics",
    conceptTo: "/11th/computer/legal-ethics/",
    chapterTo: "/11th/computer/legal-ethics/",
    exercises: verifiedTopicExercises["legal-ethics"],
  },
  {
    unit: "digital-literacy",
    unitLabel: "Digital Literacy",
    topic: "digital-literacy",
    topicLabel: "Digital Literacy",
    conceptTo: "/11th/computer/digital-literacy/",
    chapterTo: "/11th/computer/digital-literacy/",
    exercises: verifiedTopicExercises["digital-literacy"],
  },
  {
    unit: "entrepreneurship",
    unitLabel: "Entrepreneurship",
    topic: "entrepreneurship",
    topicLabel: "Entrepreneurship",
    conceptTo: "/11th/computer/entrepreneurship/",
    chapterTo: "/11th/computer/entrepreneurship/",
    exercises: verifiedTopicExercises.entrepreneurship,
  },
];

function attachMeta<T extends ExerciseShort | ExerciseLong>(items: T[], source: PracticeSource) {
  return items.map((item) => ({
    ...item,
    unit: source.unit,
    unitLabel: source.unitLabel,
    topic: source.topic,
    topicLabel: source.topicLabel,
    conceptTo: source.conceptTo,
    chapterTo: source.chapterTo,
  }));
}

export const practiceMcqs: PracticeMcq[] = computerMcqChapters.flatMap((chapter) =>
  chapter.questions.map((question) => ({
    ...question,
    difficulty: question.difficulty ?? "easy",
    unit: chapter.unit,
    unitLabel: chapter.shortTitle,
    topic: question.topic ?? chapter.unit,
    topicLabel: question.topicLabel ?? chapter.shortTitle,
    conceptTo: question.conceptTo ?? chapter.routes.chapterTo,
    chapterTo: question.chapterTo ?? chapter.routes.chapterTo,
  })),
);
export const practiceShortQuestions: PracticeShort[] = practiceSources.flatMap((source) => attachMeta(source.exercises.short, source));
export const practiceLongQuestions: PracticeLong[] = practiceSources.flatMap((source) => attachMeta(source.exercises.long, source));
